import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/modules/shared/ui/components/card'
import { Button } from '@/modules/shared/ui/components/button'
import { Textarea } from '@/modules/shared/ui/components/textarea'
import { Checkbox } from '@/modules/shared/ui/components/checkbox'
import { Label } from '@/modules/shared/ui/components/label'
import { Loader2, Info, Send, Eye } from 'lucide-react'
import { useSuggestCandidate } from '../../hooks/useVacancySuggestions'
import { useCurrentOrganization } from '@/modules/organizations/hooks/use-organizations'
import type { VacancySuggestionDto } from '../../dtos/vacancy-suggestion.dto'

const MAX_MESSAGE_LENGTH = 2000

interface Props {
  suggestion: VacancySuggestionDto
  onSuggested: (updated: VacancySuggestionDto) => void
  onCancel: () => void
}

export function SuggestCandidatePanel({ suggestion, onSuggested, onCancel }: Props) {
  const [recruiterMessage, setRecruiterMessage] = useState('')
  const [includeSalaryInMessage, setIncludeSalaryInMessage] = useState(false)

  const suggestMutation = useSuggestCandidate()
  const organization = useCurrentOrganization()
  const candidateFirstName = suggestion.candidateName.split(' ')[0] || suggestion.candidateName

  const handleSubmit = async () => {
    const updated = await suggestMutation.mutateAsync({
      suggestionId: suggestion.id,
      recruiterMessage: recruiterMessage.trim() || null,
      includeSalaryInMessage,
    })
    onSuggested(updated)
  }

  return (
    <Card className="bg-card shadow-md border-border">
      <CardHeader className="border-b border-border">
        <CardTitle className="text-lg text-foreground flex items-center gap-2">
          <Send className="w-5 h-5 text-primary" />
          Suggest for{' '}
          <span className="text-primary">{suggestion.targetVacancyTitle}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 pt-5">
        <div className="space-y-2">
          <Label htmlFor="recruiter-message">
            Message to candidate{' '}
            <span className="text-muted-foreground font-normal">(optional)</span>
          </Label>
          <Textarea
            id="recruiter-message"
            value={recruiterMessage}
            onChange={(e) => setRecruiterMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
            placeholder="Add a personal note explaining why this role might be a great fit…"
            className="min-h-[100px] resize-none"
            maxLength={MAX_MESSAGE_LENGTH}
          />
          <p className="text-xs text-muted-foreground text-right">
            {recruiterMessage.length}/{MAX_MESSAGE_LENGTH}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Checkbox
            id="include-salary"
            checked={includeSalaryInMessage}
            onCheckedChange={(checked) => setIncludeSalaryInMessage(checked === true)}
          />
          <Label htmlFor="include-salary" className="cursor-pointer">
            Include the salary range in the candidate&apos;s email
          </Label>
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-1.5 text-muted-foreground">
            <Eye className="w-3.5 h-3.5" />
            Preview of the candidate&apos;s email
          </Label>
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 space-y-2">
            <p className="text-sm text-foreground">
              Hi {candidateFirstName}, someone thinks you could be a great fit for a new role.
            </p>
            <p className="text-sm font-semibold text-foreground">
              {suggestion.targetVacancyTitle}
              {organization?.name && (
                <span className="font-normal text-muted-foreground"> at {organization.name}</span>
              )}
            </p>
            {suggestion.vacancySummary && (
              <p className="text-xs text-muted-foreground leading-relaxed">
                {suggestion.vacancySummary}
              </p>
            )}
            {includeSalaryInMessage && suggestion.salaryRange && (
              <p className="text-xs font-medium text-foreground">
                Salary range: {suggestion.salaryRange}
              </p>
            )}
            <p className="text-xs text-muted-foreground italic border-l-2 border-border pl-2">
              {recruiterMessage.trim() || 'No personal message added.'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-800">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            All active team members will be notified by email when you send this suggestion.
          </span>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <Button
            onClick={handleSubmit}
            disabled={suggestMutation.isPending}
            className="flex-1"
          >
            {suggestMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send suggestion
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={suggestMutation.isPending}
            className="flex-1 sm:flex-none"
          >
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
